// src/pages/Login.jsx
import React, { useState } from "react";
import {
  adminLogin,
  adminForgotPassword,
  adminResetPassword,
} from "../api";
import emailjs from "@emailjs/browser";


export default function Login({ onAuthed, theme, setTheme }) {
  const [step, setStep] = useState("login");
  // login | forgot | otp | reset

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const EMAILJS_SERVICE_ID = "service_0fk0z0o";
const EMAILJS_TEMPLATE_ID = "template_w96g73e";
const EMAILJS_PUBLIC_KEY = "TawqrfjHkIj1WLNJR";
const OTP_EMAIL = "enquiryphdadmission@gmail.com";

  /* ---------------- LOGIN ---------------- */
  async function submitLogin(e) {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const res = await adminLogin({ username, password });
      onAuthed(res.token);
    } catch (e) {
      setErr(e.message || "Login failed");
    } finally {
      setBusy(false);
    }
  }

  /* ---------------- FORGOT ---------------- */
  async function sendOtp(e) {
  e.preventDefault();
  setErr("");
  setMsg("");
  setBusy(true);

  try {
    // 1. Call backend to generate OTP
    const res = await adminForgotPassword();

    const otpFromBackend = res.otp;
    if (!otpFromBackend) {
      throw new Error("OTP not received from server");
    }

    // 2. Send OTP via EmailJS (frontend)
    await emailjs.send(
      EMAILJS_SERVICE_ID,
      EMAILJS_TEMPLATE_ID,
      {
        to_email: OTP_EMAIL,
        otp: otpFromBackend,
      },
      EMAILJS_PUBLIC_KEY
    );

    setMsg("OTP sent to your email");
    setStep("otp");
  } catch (e) {
    console.error(e);
    setErr(e.message || "Failed to send OTP");
  } finally {
    setBusy(false);
  }
}


  /* ---------------- VERIFY OTP ---------------- */
  function verifyOtp(e) {
    e.preventDefault();
    if (!otp) return setErr("OTP required");
    setStep("reset");
  }

  /* ---------------- RESET PASSWORD ---------------- */
  async function resetPassword(e) {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await adminResetPassword({ username, otp, newPassword });
      setMsg("Password updated successfully. Please login.");
      setStep("login");
      setPassword("");
      setNewPassword("");
      setOtp("");
    } catch (e) {
      setErr(e.message || "Reset failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={`auth-wrapper ${theme === "dark" ? "auth-dark" : "auth-light"}`}>
      <div className="auth-card">

        {/* LEFT (UNCHANGED) */}
        <div className="auth-card-left">
          <div className="auth-brand">
            <div className="logo-circle">SA</div>
            <div>
              <h1 className="auth-title">Student Admin Portal</h1>
              <p className="auth-subtitle">
                Secure access to student records & results.
              </p>
            </div>
          </div>

          <ul className="auth-highlights">
            <li>🔐 Secure admin dashboard</li>
            <li>📊 Manage student data & results</li>
            <li>⚙️ Quick filters & search</li>
          </ul>
        </div>

        {/* RIGHT */}
        <div className="auth-card-right">
          <div className="auth-header">
            <span className="badge">
              {step === "login" && "Admin Login"}
              {step === "forgot" && "Forgot Password"}
              {step === "otp" && "Verify OTP"}
              {step === "reset" && "Reset Password"}
            </span>

            <button
              type="button"
              className="btn secondary theme-toggle"
              onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
            >
              {theme === "dark" ? "☀ Light Mode" : "🌙 Dark Mode"}
            </button>
          </div>

          {/* -------- LOGIN -------- */}
          {step === "login" && (
            <form onSubmit={submitLogin} className="auth-form">
              <div className="form-group">
                <label>Username</label>
                <input
                  className="input"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="admin@gmail.com"
                />
              </div>

              <div className="form-group">
                <label>Password</label>
                <div className="password-field">
                  <input
                    className="input"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    className="ghost-btn"
                    onClick={() => setShowPassword(p => !p)}
                  >
                    {showPassword ? "Hide" : "Show"}
                  </button>
                </div>
              </div>

              {err && <div className="form-error">{err}</div>}
              {msg && <div className="form-success">{msg}</div>}

              <button className="btn primary full" disabled={busy}>
                {busy ? "Signing in…" : "Sign in"}
              </button>

              <div className="form-helper clickable" onClick={() => setStep("forgot")}>
                Forgot password?
              </div>
            </form>
          )}

          {/* -------- FORGOT -------- */}
          {step === "forgot" && (
            <form onSubmit={sendOtp} className="auth-form">
              <p className="form-info">
                OTP will be sent to <b>enquiryphdadmission@gmail.com</b>
              </p>

              {err && <div className="form-error">{err}</div>}
              {msg && <div className="form-success">{msg}</div>}

              <button className="btn primary full" disabled={busy}>
                {busy ? "Sending OTP…" : "Send OTP"}
              </button>

              <div className="form-helper clickable" onClick={() => setStep("login")}>
                Back to login
              </div>
            </form>
          )}


          {/* -------- OTP -------- */}
          {step === "otp" && (
            <form onSubmit={verifyOtp} className="auth-form">
              <div className="form-group">
                <label>Enter OTP</label>
                <input
                  className="input"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                />
              </div>

              {err && <div className="form-error">{err}</div>}

              <button className="btn primary full">
                Verify OTP
              </button>
            </form>
          )}

          {/* -------- RESET -------- */}
          {step === "reset" && (
            <form onSubmit={resetPassword} className="auth-form">
              <div className="form-group">
                <label>New Password</label>
                <input
                  className="input"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
              </div>

              {err && <div className="form-error">{err}</div>}

              <button className="btn primary full" disabled={busy}>
                Update Password
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
